import React, { useEffect, useRef, useState } from 'react';
import { servicesData, heroImages } from '../data/mock';

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative bg-[#0d0b09] py-16 md:py-24 lg:py-32 px-4 md:px-6 overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url(${heroImages[1]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(100%)'
        }}
      />

      <div className="container mx-auto max-w-5xl relative z-10">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Divider */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
            <div className="mx-4 w-2 h-2 rotate-45 border border-[#8B0000]" />
            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
          </div>

          <h2 className="text-[#A69080] text-2xl md:text-3xl tracking-[0.3em] uppercase mb-4">
            {servicesData.title}
          </h2>

          {/* Divider */}
          <div className="flex items-center justify-center mt-8">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
            <div className="mx-4 w-2 h-2 rotate-45 border border-[#D4A853]" />
            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
          </div>

          <p className="text-[#8B7355] mt-8 text-base md:text-lg max-w-3xl mx-auto">
            {servicesData.description}
          </p>
        </div>

        {/* Available Kinks */}
        <div
          className={`mb-16 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-[#D4A853] text-lg md:text-xl tracking-[0.15em] md:tracking-[0.2em] uppercase text-center mb-6 md:mb-8">
            Available Experiences
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
            {servicesData.kinks.map((kink, index) => (
              <div
                key={index}
                className="p-2 md:p-3 border border-[#8B7355]/20 text-center hover:border-[#D4A853]/50 hover:bg-[#D4A853]/5 transition-all duration-300"
              >
                <span className="text-[#A69080] text-xs md:text-sm tracking-wide">{kink}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
          <div className="mx-4 w-2 h-2 rotate-45 border border-[#6B7B4B]" />
          <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
        </div>

        {/* Session Rates */}
        <div
          className={`transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-[#D4A853] text-xl tracking-[0.2em] uppercase text-center mb-8">
            Session Rates
          </h3>
          <div className="space-y-4">
            {servicesData.rates.map((service, index) => (
              <div
                key={service.name}
                className="group flex items-center justify-between py-6 border-b border-[#8B7355]/20 hover:border-[#D4A853]/50 transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                  <span className="text-[#A69080] text-lg md:text-xl tracking-wider group-hover:text-[#D4A853] transition-colors">
                    {service.name}
                  </span>
                  <span className="text-[#8B7355] text-sm tracking-widest">
                    {service.duration}
                  </span>
                </div>
                <span className="text-[#6B7B4B] text-lg md:text-xl tracking-wider font-light">
                  {service.price}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-12 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-10 py-4 border border-[#D4A853] text-[#D4A853] text-sm tracking-[0.2em] uppercase hover:bg-[#D4A853] hover:text-[#0a0908] transition-all duration-300"
          >
            Apply to Serve
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
